import { useCallback, useState } from 'react';
import { TermsDocumentPublic } from '../types';

export type DownloadReason =
  | 'arabic-unavailable-fallback-english'
  | 'generation-failed';

export type DownloadResult =
  | { ok: true; fileName: string; reason?: 'arabic-unavailable-fallback-english' }
  | { ok: false; reason: 'generation-failed' };

const slugify = (input: string): string =>
  input
    .toLowerCase()
    .replace(/[^a-z0-9؀-ۿ]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 60) || 'document';

const A4_WIDTH_PX = 794;

export interface UseTermsDownload {
  isDownloading: boolean;
  downloadingDocId: string | null;
  download: (
    doc: TermsDocumentPublic,
    lang: 'en' | 'ar',
  ) => Promise<DownloadResult>;
}

export function useTermsDownload(): UseTermsDownload {
  const [downloadingDocId, setDownloadingDocId] = useState<string | null>(null);

  const download = useCallback(
    async (doc: TermsDocumentPublic, lang: 'en' | 'ar'): Promise<DownloadResult> => {
      const wantsArabic = lang === 'ar';
      const arabicMissing = wantsArabic && !doc.bodyAr;
      const effectiveLang: 'en' | 'ar' = arabicMissing ? 'en' : lang;
      const title = effectiveLang === 'ar' && doc.titleAr ? doc.titleAr : doc.title;
      const body = effectiveLang === 'ar' && doc.bodyAr ? doc.bodyAr : doc.body;
      const fileName = `${slugify(title)}-${effectiveLang}.pdf`;

      setDownloadingDocId(doc.id);

      const container = document.createElement('div');
      container.setAttribute('dir', effectiveLang === 'ar' ? 'rtl' : 'ltr');
      container.style.position = 'fixed';
      container.style.left = '-9999px';
      container.style.top = '0';
      container.style.width = `${A4_WIDTH_PX}px`;
      container.style.padding = '32px';
      container.style.background = '#ffffff';
      container.style.color = '#1a1a2e';
      container.style.fontFamily =
        "'Alexandria', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif";
      container.style.fontSize = '14px';
      container.style.lineHeight = '1.7';
      container.innerHTML = `<h1 style="font-size:22px;margin:0 0 16px 0;color:#030c54;">${escapeHtml(title)}</h1><div>${body}</div>`;
      document.body.appendChild(container);

      try {
        const [{ default: jsPDF }, { default: html2canvas }] = await Promise.all([
          import('jspdf'),
          import('html2canvas'),
        ]);

        const canvas = await html2canvas(container, {
          backgroundColor: '#ffffff',
          scale: 2,
          useCORS: true,
          logging: false,
        });

        const pdf = new jsPDF({ unit: 'mm', format: 'a4', orientation: 'portrait' });
        const pageWidthMm = pdf.internal.pageSize.getWidth();
        const pageHeightMm = pdf.internal.pageSize.getHeight();
        const pxPerMm = canvas.width / pageWidthMm;
        const pageHeightPx = Math.floor(pageHeightMm * pxPerMm);

        let renderedPx = 0;
        let pageIndex = 0;
        while (renderedPx < canvas.height) {
          const slice = document.createElement('canvas');
          const sliceHeight = Math.min(pageHeightPx, canvas.height - renderedPx);
          slice.width = canvas.width;
          slice.height = sliceHeight;
          const ctx = slice.getContext('2d');
          if (!ctx) throw new Error('canvas 2d context unavailable');
          ctx.drawImage(
            canvas,
            0,
            renderedPx,
            canvas.width,
            sliceHeight,
            0,
            0,
            canvas.width,
            sliceHeight,
          );

          if (pageIndex > 0) pdf.addPage();
          const sliceHeightMm = sliceHeight / pxPerMm;
          pdf.addImage(slice.toDataURL('image/png'), 'PNG', 0, 0, pageWidthMm, sliceHeightMm);

          renderedPx += sliceHeight;
          pageIndex += 1;
        }

        pdf.save(fileName);

        return arabicMissing
          ? { ok: true, fileName, reason: 'arabic-unavailable-fallback-english' }
          : { ok: true, fileName };
      } catch {
        return { ok: false, reason: 'generation-failed' };
      } finally {
        document.body.removeChild(container);
        setDownloadingDocId(null);
      }
    },
    [],
  );

  return {
    isDownloading: downloadingDocId !== null,
    downloadingDocId,
    download,
  };
}

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
}
